import { useState } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCatalogAssembly } from "@/hooks/use-catalog-assemblies";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useCatalogShutoffValves } from "@/hooks/use-catalog-shutoff-valves";
import { useCatalogDetectorMeters } from "@/hooks/use-catalog-detector-meters";
import { Breadcrumbs, EmptyState, PageHeader, Skeleton } from "@/components/ui";
import { AssemblyDetail } from "@/components/catalog/assembly-detail";

export const Route = createFileRoute("/_authenticated/catalog/assemblies/$id")({
  component: CatalogAssemblyDetailPage,
});

function CatalogAssemblyDetailPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);
  const { id } = useParams({ from: "/_authenticated/catalog/assemblies/$id" });

  // Install-date filter for the ListingResolver. State lives here
  // (route shell) and is forwarded to both the hook (so the cache key
  // includes the date and each selection produces a distinct cache
  // entry) AND the detail component (so the date input can render
  // with the current value).
  const [installDate, setInstallDate] = useState<string | undefined>(undefined);

  const assemblyQuery = useCatalogAssembly(id, { installDate });
  useRecentPageTitle(assemblyQuery.data?.model ?? undefined);
  // Hydrate the descriptions for shutoff_valve_codes / detector_meter_codes
  // client-side. Both lookup tables are ≤167 rows; pass `limit=200` so a
  // single roundtrip retrieves the entire table per the design doc's
  // "list response hydration policy".
  const shutoffValvesQuery = useCatalogShutoffValves({ limit: 200 });
  const detectorMetersQuery = useCatalogDetectorMeters({ limit: 200 });

  if (assemblyQuery.isLoading) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton variant="line" />
        <Skeleton variant="card" />
      </div>
    );
  }

  if (assemblyQuery.isError || !assemblyQuery.data) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[
            { label: t("catalog.group", { ns: "nav" }) },
            { label: t("page.assembliesTitle"), to: "/catalog/assemblies" },
            { label: t("page.assemblyDetailTitle") },
          ]}
        />
        <PageHeader title={t("page.assemblyDetailTitle")} />
        <div className="mt-6">
          <EmptyState title={t("page.assemblyDetailTitle")} description={t("errors.loadFailed")} />
        </div>
      </div>
    );
  }

  const assembly = assemblyQuery.data;
  const shutoffValves = shutoffValvesQuery.data?.items ?? [];
  const detectorMeters = detectorMetersQuery.data?.items ?? [];

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          { label: t("page.assembliesTitle"), to: "/catalog/assemblies" },
          { label: assembly.model },
        ]}
      />
      <PageHeader
        title={assembly.model}
        subtitle={`${assembly.manufacturer_name} — ${t(`assemblyType.${assembly.assembly_type}`)}`}
      />

      <div className="mt-6">
        <AssemblyDetail
          assembly={assembly}
          shutoffValves={shutoffValves}
          detectorMeters={detectorMeters}
          installDate={installDate}
          onInstallDateChange={setInstallDate}
        />
      </div>
    </div>
  );
}
